/**
 * Single source of truth for app navigation.
 *
 * Shared by the sidebar and the command palette. Grouped by **what the user is
 * doing** (research → books → analysis → risk → …) rather than by which module
 * a page happens to live in.
 *
 * Routes are intentionally left untouched: regrouping is a labelling change, so
 * existing bookmarks and the in-app Tier 1/2 documentation keep working.
 */
import {
  Home, Search, FileText, Trophy, Target, GitBranch, Network, Microscope,
  Wallet, Gem, FolderOpen, PieChart, TrendingUp, BarChart3, Activity,
  LineChart, Shield, Zap, FlaskConical, FileBarChart, DollarSign, UserCheck,
  Banknote, Database, Cloud, Settings, Upload, User, Users, Cpu, BookOpen, Bot,
} from 'lucide-react'

export interface NavItem {
  name: string
  href: string
  icon: React.ElementType
  adminOnly?: boolean
  /** extra words the command palette should match on */
  keywords?: string
}

export interface NavSection {
  title: string
  items: NavItem[]
}

export const navigation: NavSection[] = [
  {
    title: 'Home',
    items: [
      { name: 'Dashboard', href: '/', icon: Home, keywords: 'home overview start' },
      { name: 'Documentation', href: '/documentation', icon: BookOpen, keywords: 'docs help guide manual' },
    ],
  },
  {
    title: 'Research',
    items: [
      { name: 'Fund Screening', href: '/screening', icon: Search, keywords: 'screen filter find funds universe' },
      { name: 'Fund Identity', href: '/fund', icon: FileText, keywords: 'profile details fund card' },
      { name: "What's Working", href: '/whats-working', icon: Trophy, keywords: 'performance winners hedge fund leaders' },
      { name: 'Performance Analytics', href: '/performance-analytics', icon: Target, keywords: 'pareto contribution win loss hit rate payoff most opened attention' },
      { name: 'Peer Group', href: '/peer-group', icon: GitBranch, keywords: 'peers comparison ranking cohort' },
      { name: 'Network Analysis', href: '/network', icon: Network, keywords: 'correlation screening graph clusters' },
      { name: 'Fund Risk Analysis', href: '/fund-analysis', icon: Microscope, keywords: 'quant risk single fund deep dive' },
    ],
  },
  {
    title: 'Books',
    items: [
      { name: 'Portfolio Book', href: '/book', icon: Wallet, keywords: 'pms holdings positions transactions ledger nav liquid' },
      { name: 'Private Markets', href: '/private', icon: Gem, keywords: 'private assets vc pe angel tvpi dpi xirr pme waterfall' },
      { name: 'Demo Portfolios', href: '/portfolio/demo', icon: FolderOpen, keywords: 'sample scratch weights' },
    ],
  },
  {
    title: 'Analysis',
    items: [
      { name: 'Portfolio Analysis', href: '/portfolio', icon: PieChart, keywords: 'correlation exposure pca uniqueness hhi' },
      { name: 'Portfolio Simulation', href: '/portfolio/simulation', icon: TrendingUp, keywords: 'simulate what-if optimise' },
      { name: 'Portfolio Returns', href: '/portfolio/returns', icon: BarChart3, keywords: 'returns monitoring performance' },
      { name: 'Factor Modeling', href: '/factors', icon: BarChart3, keywords: 'factors regression hac benjamini exposure betas' },
      { name: 'Factor Attribution', href: '/factors/attribution', icon: TrendingUp, keywords: 'attribution contribution factors' },
    ],
  },
  {
    title: 'Risk',
    items: [
      { name: 'Risk Dashboard', href: '/risk', icon: Activity, keywords: 'risk overview diversification stress kpi' },
      { name: 'Overview', href: '/risk-stats', icon: LineChart, keywords: 'statistical risk overview' },
      { name: 'VaR', href: '/risk-stats/var', icon: TrendingUp, keywords: 'value at risk cvar expected shortfall' },
      { name: 'Stress', href: '/risk-stats/stress', icon: Activity, keywords: 'stress scenario shock crisis' },
      { name: 'Factor Model', href: '/risk-stats/factor-model', icon: BarChart3, keywords: 'risk factor model' },
      { name: 'Attribution', href: '/risk-stats/attribution', icon: TrendingUp, keywords: 'risk attribution' },
      { name: 'Backtest', href: '/risk-stats/backtest', icon: Shield, keywords: 'backtest validation' },
      { name: 'Peers', href: '/risk-stats/peers', icon: GitBranch, keywords: 'risk peers alpha beta rolling sharpe' },
      { name: 'Ensemble', href: '/risk-stats/ensemble', icon: Network, keywords: 'ensemble models blend' },
      { name: 'Report View', href: '/risk-stats/report', icon: FileText, keywords: 'risk report' },
      { name: 'Run Registry', href: '/risk-stats/runs', icon: Zap, keywords: 'runs jobs history registry' },
    ],
  },
  {
    title: 'Strategy Lab',
    items: [
      { name: 'AI CIO', href: '/ai-cio', icon: Bot, keywords: 'ai cio chat agent assistant llm ask question langgraph tools knowledge base' },
      { name: 'Strategy Lab', href: '/strategy-lab', icon: FlaskConical, keywords: 'selection construction cvar bayesian posterior crowding absorption effective bets rebalance optimiser' },
      { name: 'Manager Selection', href: '/manager-selection', icon: FlaskConical, keywords: 'hedge fund manager selection peer group cluster smoothing volatility laundering replica alpha' },
      { name: 'Benchmark Mapping', href: '/benchmarks', icon: FlaskConical, keywords: 'benchmark mapping tail evt underlying instrument response power law' },
      { name: 'QIS Construction', href: '/qis', icon: FlaskConical, keywords: 'qis quantitative strategies universe decay psr correlation clusters' },
      { name: 'High-Freq Returns', href: '/high-freq', icon: Zap, keywords: 'intraday high frequency vrp' },
    ],
  },
  {
    title: 'Reporting',
    items: [
      { name: 'Report Center', href: '/reports', icon: FileBarChart, keywords: 'reports templates designer pdf generate' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { name: 'OPERA Reports', href: '/opera', icon: FileText, keywords: 'opera aifmd regulatory' },
      { name: 'AUM Monitoring', href: '/aum', icon: DollarSign, keywords: 'aum assets under management' },
      { name: 'Personnel', href: '/personnel', icon: UserCheck, keywords: 'people staff team' },
      { name: 'Redemption', href: '/redemption', icon: Banknote, keywords: 'redemption liquidity calculator' },
      { name: 'Doc Completeness', href: '/data/docs', icon: Shield, keywords: 'documents completeness checklist' },
    ],
  },
  {
    title: 'Data',
    items: [
      { name: 'Instruments', href: '/data/instruments', icon: BarChart3, keywords: 'tickers prices instruments' },
      { name: 'Data Explorer', href: '/data/explorer', icon: Database, keywords: 'explorer datasets providers worklist' },
      { name: 'Data Validation', href: '/data/validation', icon: Database, keywords: 'validation quality checks' },
      { name: 'Insert Returns', href: '/data/returns', icon: Database, keywords: 'insert returns manual entry' },
      { name: 'Data Providers', href: '/data/providers', icon: Cloud, keywords: 'providers fmp bloomberg albourne lumrisk' },
      { name: 'Fund Management', href: '/data/funds', icon: Settings, keywords: 'manage funds master' },
      { name: 'Fund Upload', href: '/data/upload', icon: Upload, keywords: 'upload import funds' },
    ],
  },
  {
    title: 'Account',
    items: [
      { name: 'Profile', href: '/profile', icon: User, keywords: 'account profile me' },
      { name: 'User Management', href: '/admin/users', icon: Users, adminOnly: true, keywords: 'users admin permissions' },
      { name: 'Job Queue', href: '/admin/jobs', icon: Cpu, adminOnly: true, keywords: 'jobs queue arq worker' },
    ],
  },
]

/** Flat list (group title attached) — used by the command palette. */
export const flatNavigation = navigation.flatMap(s =>
  s.items.map(i => ({ ...i, section: s.title }))
)

/** The section that owns a given pathname (longest matching href wins). */
export function sectionForPath(pathname: string): string | null {
  let best: { len: number; title: string } | null = null
  for (const s of navigation) {
    for (const i of s.items) {
      const match = i.href === '/' ? pathname === '/' : pathname.startsWith(i.href)
      if (match && (!best || i.href.length > best.len)) best = { len: i.href.length, title: s.title }
    }
  }
  return best?.title ?? null
}
